import { getNextColor } from "@fluentui/react-charting";
import { Card, FluentProvider, makeStyles, shorthands, tokens, webLightTheme } from "@fluentui/react-components";
import { GridColDef } from "@mui/x-data-grid";
import { useCallback, useMemo, useState } from "react";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { CustomLinkNavigate } from "../../../../SharedComponents/CustomLinkNavigate";
import { CustomMetricsCard } from "../../../../SharedComponents/CustomMetricsCard";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";
import { PartnerInfluenceData, PartnerRow, PartnerSegmentTab, UsageTrendPoint } from "./partnerInfluence.interfaces";
import PartnerTenantsChart from "./PartnerTenantsChart";
import UsageTrendChart from "./UsageTrendChart";
import { formatDate } from "../../../../helper/DateFormat";

const PARTNER_SEGMENT_OPTIONS: { key: PartnerSegmentTab; text: string }[] = [
  { key: "isElite", text: "IsElite" },
  { key: "nonElite", text: "Top 200 NonElite" }
];

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalL),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL)
  },
  kpiRow: {
    display: "flex",
    flexWrap: "wrap",
    ...shorthands.gap("16px"),
    ...shorthands.padding("10px")
  },
  kpiCard: {
    flex: "1 1 220px",
    minWidth: "200px",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow2
  },
  kpiLabel: {
    display: "block",
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    marginBottom: tokens.spacingVerticalXS
  },
  kpiValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    alignItems: "stretch",
    ...shorthands.gap(tokens.spacingHorizontalL),
    "@media (min-width: 960px)": {
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)"
    }
  },
  chartCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow2
  },
  chartTitle: {
    display: "block",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalS
  },
  gridSection: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    boxShadow: tokens.shadow2
  },
  gridContainer: {
    height: "520px",
    width: "100%",
    marginTop: tokens.spacingVerticalM
  }
});

const navigateToPartner = (partnerOneId: number) => {
  return `/partners?partnerOneId=${encodeURIComponent(partnerOneId)}&mpnId=AllUp`;
};

const toNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return Number.isNaN(value) ? 0 : value;
  }

  const parsed = Number(value.toString().replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const ConsumptionTab = ({
  data,
  isLoading,
  productName
}: {
  data?: PartnerInfluenceData;
  isLoading: boolean;
  productName: string;
}) => {
  const styles = useStyles();
  const isSentinel = productName.toLowerCase() === "microsoft sentinel";
  const [segment, setSegment] = useState<PartnerSegmentTab>("isElite");
  const getRowId = useCallback((row: PartnerRow & { id: string }) => row.id, []);

  const usageLicensesDetails = useMemo<UsageTrendPoint[]>(
    () =>
      (data?.usageLicensesDetails || []).map((item) => ({
        ...item,
        snapshotDate: new Date(item.snapshotDate),
        consumedSCU: toNumber(item.consumedSCU),
        consumedSCU_IsElite: toNumber(item.consumedSCU_IsElite),
        consumedSCU_NonElite: toNumber(item.consumedSCU_NonElite)
      })),
    [data?.usageLicensesDetails]
  );

  const partnerTenantsDetails = useMemo(
    () =>
      (data?.partnerTenantsDetails || []).map((item) => ({
        ...item,
        snapshotDate: new Date(item.snapshotDate),
        partners_IsElite: toNumber(item.partners_IsElite),
        partners_NonElite: toNumber(item.partners_NonElite),
        tenantCount: toNumber(item.tenantCount)
      })),
    [data?.partnerTenantsDetails]
  );

  const renderSegmentTabs = () => (
    <div style={{ display: "inline-flex", gap: 8, marginBottom: 8 }}>
      {PARTNER_SEGMENT_OPTIONS.map((opt) => {
        const isSelected = segment === opt.key;
        return (
          <button
            key={opt.key}
            onClick={() => setSegment(opt.key)}
            style={{
              padding: "6px 20px",
              border: isSelected ? "1px solid #0078D4" : "1px solid #E1DFDD",
              borderRadius: "20px",
              backgroundColor: isSelected ? "#E3F2FD" : "#FFFFFF",
              color: isSelected ? "#0078D4" : "#605E5C",
              cursor: "pointer",
              fontSize: "13px",
              fontWeight: isSelected ? 600 : 400,
              transition: "all 0.2s ease",
              outline: "none"
            }}
            title={opt.text}
          >
            {opt.text}
          </button>
        );
      })}
    </div>
  );

  const partnerRows = useMemo(() => {
    // Filter partners based on selected segment
    // Note: isElite can be boolean or string ("true"/"false") from the API
    const isEliteValue = (value: boolean | string): boolean => {
      if (typeof value === "boolean") return value;
      return String(value).toLowerCase() === "true";
    };
    const filtered = (data?.partnerInfluenceDetails || []).filter((p: PartnerRow) =>
      segment === "isElite" ? isEliteValue(p.isElite) : !isEliteValue(p.isElite)
    );

    // Remove commas from partnerOneId for all rows to ensure proper navigation and sorting
    // Add stable unique id using index for grid row identification
    // Convert snapshotDate to Date object for proper date filtering
    const mapped = filtered.map((p: PartnerRow, index: number) => ({
      ...p,
      id: `${segment}-${index}`,
      partnerOneId: p.partnerOneId ?? 0 // Ensure partnerOneId is a number, default to 0 if null or undefined
        ? Number(p.partnerOneId.toString().replace(/,/g, ""))
        : 0,
      currentValue: toNumber(p.currentValue),
      tenantCount: toNumber(p.tenantCount),
      snapshotDate: p.snapshotDate ? new Date(new Date(p.snapshotDate).toISOString().slice(0, 10)) : null
    }));

    // For NonElite, sort by the product's usage metric and limit to top 200.
    if (segment === "nonElite") {
      return mapped.sort((a, b) => b.currentValue - a.currentValue).slice(0, 200);
    }
    return mapped;
  }, [segment, data]);

  const getValue = (segment: PartnerSegmentTab) => {
    const recentDate = usageLicensesDetails?.reduce((latest: Date, item: UsageTrendPoint) => {
      const itemDate = new Date(item.snapshotDate);
      return itemDate > latest ? itemDate : latest;
    }, new Date(0));

    const recentData = usageLicensesDetails?.find(
      (item: UsageTrendPoint) => new Date(item.snapshotDate).getTime() === recentDate.getTime()
    );
    if (!recentData) return "0%";

    const eliteValue = toNumber(recentData.consumedSCU_IsElite);
    const nonEliteValue = toNumber(recentData.consumedSCU_NonElite);
    const total = isSentinel ? eliteValue + nonEliteValue : toNumber(recentData.consumedSCU);
    const segmentValue =
      segment === "isElite"
        ? eliteValue
        : nonEliteValue;
    if (!total || Number.isNaN(segmentValue)) return "0%";
    const value = (segmentValue / total) * 100;
    return `${value.toFixed(isSentinel ? 1 : 2)}%`;
  };

  // Custom quick filter function for searching numeric/formatted columns as strings
  const getApplyQuickFilterFn = (value: string) => {
    if (!value) return null;
    const searchLower = value.toLowerCase();
    return (cellValue: any) => {
      if (cellValue === null || cellValue === undefined) return false;
      return String(cellValue).toLowerCase().includes(searchLower);
    };
  };

  const columns: GridColDef<PartnerRow>[] = useMemo(
    () => [
      {
        headerName: "Partner Name",
        field: "partnerOneName",
        flex: 1.5,
        minWidth: 200,
        renderCell: ({ row }) => (
          <CustomLinkNavigate to={navigateToPartner(row.partnerOneId)} text={row.partnerOneName} />
        )
      },
      {
        headerName: "ID",
        field: "partnerOneId",
        flex: 1,
        minWidth: 140,
        type: "number",
        renderCell: ({ row }) => <span>{row.partnerOneId}</span>,
        getApplyQuickFilterFn
      },
      {
        headerName: isSentinel ? "Ingested Volume" : "ConsumedSCU",
        field: "currentValue",
        type: "number",
        flex: 1,
        minWidth: 160,
        valueFormatter: (value: number) => value?.toLocaleString("en-US", { maximumFractionDigits: 0 }),
        getApplyQuickFilterFn
      },
      {
        headerName: isSentinel ? "Customers Count" : "Tenant Count",
        field: "tenantCount",
        type: "number",
        flex: 1,
        minWidth: 160,
        valueFormatter: (value: number) => value?.toLocaleString("en-US", { maximumFractionDigits: 0 }),
        getApplyQuickFilterFn
      },
      {
        headerName: "Snapshot Date",
        field: "snapshotDate",
        type: "date",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => {
          const dateValue = params.row.snapshotDate;
          if (!dateValue) return "-";
          return formatDate(new Date(dateValue).toISOString());
        }
      }

    ],
    [isSentinel]
  );

  const renderKpiCards = () => {
    if (isLoading) {
      return <CustomShimmer rowCount={1} columnCount={4} styles={PRODUCT_VIEW_STYLES.OVERVIEW_SECTOIN.shimmerStyles} />;
    }

    return (
      <div className={styles.kpiRow}>
        <CustomMetricsCard
          title={isSentinel ? "Ingested Volume IsElite %" : "ConsumedSCU IsElite %"}
          description={isSentinel ? "Percentage of partner-influenced ingested volume that is IsElite." : "Percentage of consumed SCU that is IsElite."}
          currentValue={getValue("isElite")}
          abbrevatedValue={getValue("isElite")}
          color={getNextColor(0)}
        />
        <CustomMetricsCard
          title={isSentinel ? "Ingested Volume NonElite %" : "ConsumedSCU NonElite %"}
          description={isSentinel ? "Percentage of partner-influenced ingested volume that is NonElite." : "Percentage of consumed SCU that is NonElite."}
          currentValue={getValue("nonElite")}
          abbrevatedValue={getValue("nonElite")}
          color={getNextColor(1)}
        />
      </div>
    );
  };

  return (
    <div className={styles.root}>
      {/* KPIs */}
      <FluentProvider theme={webLightTheme}>
        <Card style={PRODUCT_VIEW_STYLES.OVERVIEW_SECTOIN.cardContent}>
          {renderKpiCards()}
        </Card>
      </FluentProvider>

      {/* charts */}
      <div className={styles.chartsRow}>
        <UsageTrendChart
          data={usageLicensesDetails}
          consumedColor={isSentinel ? "#E3008C" : "#0078D4"}
          eliteColor={isSentinel ? "#0078D4" : "#E3008C"}
          nonEliteColor={isSentinel ? "#5C2D91" : "#0E7C7B"}
          consumedLegend={isSentinel ? "Ingested Volume" : undefined}
          eliteLegend={isSentinel ? "Ingested Volume IsElite" : undefined}
          nonEliteLegend={isSentinel ? "Ingested Volume NonElite" : undefined}
          isLoading={isLoading}
          kustoLink={data?.usageLicenseKustoLink}
        />
        <PartnerTenantsChart
          data={partnerTenantsDetails}
          cardTitle={isSentinel ? "Partners & Customer Trend" : undefined}
          groupBars={isSentinel}
          colors={{
            partnersIsElite: "#0078D4",
            partnersNonElite: isSentinel ? "#5C2D91" : "#7FB3E8",
            tenants: isSentinel ? "#00B7C3" : "#B7E4C7",
            avgCustomers: "#E3008C"
          }}
          legends={isSentinel ? {
            partnersIsElite: "Partners IsElite",
            partnersNonElite: "Partners NonElite",
            tenants: "Customers",
            avgCustomers: "Avg Customers/Partners"
          } : undefined}
          isLoading={isLoading}
          kustoLink={data?.partnerTenantKustoLink}
        />
      </div>

      {/* Grid Section */}
      <div className={styles.gridSection}>
        {renderSegmentTabs()}
        <div className={styles.gridContainer}>
          <CustomDataGrid
            columns={columns}
            getRowId={getRowId}
            loading={isLoading}
            paginationOptions={{ page: 0, pageSize: 25 }}
            rows={{ values: partnerRows, count: partnerRows.length }}
            showCellVerticalBorder
            exportCsvButtonProps={{
              fileNamePrefix: segment === "isElite" ? "partner-influence-iselite" : "partner-influence-top200-nonelite"
            }}
            TabName={`Product View - Partner Influence - ${segment === "isElite" ? "IsElite" : "Top 200 NonElite"}`}
            kustoLink={data?.partnerInfluenceKustoLink}
          />
        </div>
      </div>
    </div>
  );
};

export default ConsumptionTab;
